import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, User as UserIcon } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/utils';
import { useGuestTransfers, useTransfers } from '@/hooks/useTransfer';
import { useGuestJobs, useJobs } from '@/hooks/useJob';
import { useGuestFranchises, useFranchises, useFranchiseRequests, useGuestFranchiseRequests } from '@/hooks/useFranchise';
import { useGuestCandidateProfiles, useCandidateProfiles } from '@/hooks/useCandidate';
import { useGuestCompanies, useCompanies } from '@/hooks/useCompany';
import { useGuestTransferRequests, useTransferRequests } from '@/hooks/useTransferRequest';
import TransferCard from '@/components/card/TransferCard';
import JobCard from '@/components/card/JobCard';
import CompanyCard from '@/components/card/CompanyCard';
import FranchiseCard from '@/components/card/FranchiseCard';
import FranchiseRequestCard from '@/components/card/FranchiseRequestCard';
import CandidateCard from '@/components/card/CandidateCard';
import TransferRequestCard from '@/components/card/TransferRequestCard';
import RentalCard from '@/components/card/RentalCard';
import RentalRequestCard from '@/components/card/RentalRequestCard';
import { useRentalProperties, useGuestRentalProperties, useRentalRequests, useGuestRentalRequests } from '@/hooks/useRental';
import { PERMISSIONS } from '@/routes/guards';
import { Filter } from '@/types';
import { useUIStore } from '@/store/uiStore';

/* ─── Mock Data ─────────────────────────────────────────────────── */
const BANNERS = [
  {
    bg: 'linear-gradient(135deg, #fff7ed 0%, #fed7aa 60%, #fdba74 100%)',
    label: 'Nhận ưu đãi lên đến 20% nhân dịp 8/3',
    cta: 'Mua sắm ngay',
    emoji: '🎁🛒🏪',
  },
  {
    bg: 'linear-gradient(135deg, #ecfdf5 0%, #a7f3d0 60%, #6ee7b7 100%)',
    label: 'Hàng ngàn cơ hội việc làm F&B mới nhất',
    cta: 'Tìm việc ngay',
    emoji: '💼🍜🔍',
  },
  {
    bg: 'linear-gradient(135deg, #eff6ff 0%, #bfdbfe 60%, #93c5fd 100%)',
    label: 'Sang nhượng quán đang hot — Giá tốt nhất thị trường',
    cta: 'Xem ngay',
    emoji: '🏪🔑💰',
  },
];



const LIMIT = 6;

/* ─── Component ─────────────────────────────────────────────────── */
export default function HomePage() {
  const { isAuthenticated, user } = useAuthStore();
  const { searchQuery, provinceFilter } = useUIStore();

  const [bannerIdx, setBannerIdx] = useState(0);
  const banner = BANNERS[bannerIdx];

  const [filters, setFilters] = useState<Filter>({
    page: 1,
    limit: LIMIT,
    search: undefined,
    provinceId: undefined,
  });

  // Sync khi searchQuery hoặc provinceFilter thay đổi
  useEffect(() => {
    setFilters((f) => ({
      ...f,
      search: searchQuery || undefined,
      provinceId: provinceFilter || undefined,
    }));
  }, [searchQuery, provinceFilter]);

  const { data: transfers, isLoading: isLoadingTransfers, isFetching: isFetchingTransfers } = isAuthenticated
    ? useTransfers(filters)
    : useGuestTransfers(filters);

  const { data: jobs, isLoading: isLoadingJobs, isFetching: isFetchingJobs } = isAuthenticated
    ? useJobs(filters)
    : useGuestJobs(filters);

  const { data: companies, isLoading: isLoadingCompanies } = isAuthenticated
    ? useCompanies(filters)
    : useGuestCompanies(filters);

  const { data: franchises, isLoading: isLoadingFranchises, isFetching: isFetchingFranchises } = isAuthenticated
    ? useFranchises(filters)
    : useGuestFranchises(filters);

  // Job creators (headhunters) can see full candidate data
  const { data: candidates, isLoading: isLoadingCandidates, isFetching: isFetchingCandidates } = isAuthenticated
    ? useCandidateProfiles(filters)
    : useGuestCandidateProfiles(filters);


  const { data: rentals, isLoading: isLoadingRentals } = isAuthenticated
    ? useRentalProperties(filters)
    : useGuestRentalProperties(filters);
  const { data: rentalReqs, isLoading: isLoadingRentalReqs } = isAuthenticated
    ? useRentalRequests(filters)
    : useGuestRentalRequests(filters);

  const { data: transferReqs, isLoading: isLoadingTransferReqs } = isAuthenticated
    ? useTransferRequests(filters)
    : useGuestTransferRequests(filters);

  const { data: franchiseReqs, isLoading: isLoadingFranchiseReqs } = isAuthenticated
    ? useFranchiseRequests(filters)
    : useGuestFranchiseRequests(filters);

  const hotTransfers = transfers?.data ?? [];
  const hotTransferRequests = transferReqs?.data ?? [];
  const hotRentals = rentals?.data ?? [];
  const hotRentalRequests = rentalReqs?.data ?? [];
  const hotJobs = jobs?.data ?? [];
  const hotCompanies = companies?.data ?? [];
  const hotFranchises = franchises?.data ?? [];
  const hotFranchiseRequests = franchiseReqs?.data ?? [];
  const hotCandidates = candidates?.data ?? [];

  return (
    <div className="space-y-10">
      {/* ══════ HERO BANNER ══════ */}
      <section className="relative rounded-2xl overflow-hidden h-52 sm:h-64 flex items-center shadow-md" style={{ background: banner.bg }}>
        <div className="absolute left-0 top-0 h-full w-1/3 flex items-center justify-center text-6xl pointer-events-none select-none opacity-80">
          {banner.emoji.split('').map((e, i) => (
            <span key={i} className={cn('absolute', i === 0 && 'left-4 top-6', i === 1 && 'left-12 bottom-6', i === 2 && 'left-24 top-10')} style={{ fontSize: 40 + i * 10 }}>
              {e}
            </span>
          ))}
        </div>

        <div className="ml-auto mr-8 sm:mr-20 text-center z-10 max-w-md">
          <h2 className="font-display font-black text-2xl sm:text-3xl text-gray-800 mb-3 leading-tight">{banner.label}</h2>
          <Link to="/sang-nhuong" className="btn-ghost-brand text-base font-bold" style={{ color: 'var(--brand)' }}>
            {banner.cta} <ArrowRight size={16} />
          </Link>
        </div>

        {/* Dots + arrows */}
        <button
          onClick={() => setBannerIdx((i) => (i - 1 + BANNERS.length) % BANNERS.length)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white shadow text-gray-600"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={() => setBannerIdx((i) => (i + 1) % BANNERS.length)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-full bg-white/70 hover:bg-white shadow text-gray-600"
        >
          <ChevronRight size={18} />
        </button>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
          {BANNERS.map((_, i) => (
            <button key={i} onClick={() => setBannerIdx(i)} className={cn('h-1.5 rounded-full transition-all', i === bannerIdx ? 'w-5 bg-orange-500' : 'w-1.5 bg-white/60')} />
          ))}
        </div>
      </section>


      {/* ══════ SANG NHƯỢNG HOT ══════ */}
      {hotTransfers.length > 0 && (
        <section>
          <SectionHeader label="Sang nhượng" title="Sang nhượng HOT" to="/sang-nhuong" />
          <>
            <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isFetchingTransfers && 'opacity-60')}>
              {hotTransfers.slice(0, 6).map((item) => (
                <TransferCard key={item.id} item={item} />
              ))}
            </div>
          </>
        </section>
      )}

      {/* ══════ NHU CẦU MUA SANG NHƯỢNG HOT ══════ */}
      {hotTransferRequests.length > 0 && (
        <section>
          <SectionHeader label="Nhu cầu" title="Nhu cầu mua sang nhượng HOT" to="/nhu-cau-sang-nhuong" />
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isLoadingTransferReqs && 'opacity-60')}>
            {hotTransferRequests.slice(0, 6).map((item) => (
              <TransferRequestCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ══════ CHO THUÊ HOT ══════ */}
      {hotRentals.length > 0 && (
        <section>
          <SectionHeader label="Cho thuê" title="Mặt bằng cho thuê HOT" to="/cho-thue" />
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isLoadingRentals && 'opacity-60')}>
            {hotRentals.slice(0, 6).map((item) => (
              <RentalCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ══════ NHU CẦU THUÊ HOT ══════ */}
      {hotRentalRequests.length > 0 && (
        <section>
          <SectionHeader label="Nhu cầu" title="Nhu cầu thuê mặt bằng HOT" to="/nhu-cau-cho-thue" />
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isLoadingRentalReqs && 'opacity-60')}>
            {hotRentalRequests.slice(0, 6).map((item) => (
              <RentalRequestCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ══════ VIỆC LÀM HOT ══════ */}
      {hotJobs.length > 0 && (
        <section>
          <SectionHeader label="Việc làm" title="Danh sách việc làm HOT" to="/viec-lam" />

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity">
            {hotJobs.slice(0, 6).map((item) => (
              <JobCard job={item} />
            ))}
          </div>
        </section>
      )}
      {/* ══════ HOT COMPANIES ══════ */}
      {hotCompanies.length > 0 && (
        <section>
          <SectionHeader label="Việc làm" title="Danh sách công ty HOT" to="/cong-ty" />
          <div className={cn("grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 transition-opacity", isLoadingCompanies && "opacity-60")}>
            {hotCompanies.map((co) => (
              <CompanyCard key={co.id} company={co} />
            ))}
          </div>
        </section>
      )}

      {/* ══════ ỨNG VIÊN NỔI BẬT ══════ */}
      {hotCandidates.length > 0 && (
        <section>
          <SectionHeader label="Tuyển dụng" title="Hồ sơ ứng viên nổi bật" to="/ung-vien/tim-kiem" />
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isFetchingCandidates && 'opacity-60')}>
            {hotCandidates.slice(0, 6).map((item: any) => (
              <CandidateCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ══════ NHƯỢNG QUYỀN HOT ══════ */}
      {hotFranchises.length > 0 && (
        <section>
          <SectionHeader label="Nhượng quyền" title="Thương hiệu nhượng quyền HOT" to="/nhuong-quyen" />
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isFetchingFranchises && 'opacity-60')}>
            {hotFranchises.slice(0, 6).map((item: any) => (
              <FranchiseCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* ══════ NHU CẦU NHƯỢNG QUYỀN HOT ══════ */}
      {hotFranchiseRequests.length > 0 && (
        <section>
          <SectionHeader label="Nhu cầu" title="Nhu cầu nhượng quyền HOT" to="/nhu-cau-nhuong-quyen" />
          <div className={cn('grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 transition-opacity', isLoadingFranchiseReqs && 'opacity-60')}>
            {hotFranchiseRequests.slice(0, 6).map((item: any) => (
              <FranchiseRequestCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}



      {/* ══════ CTA guests ══════ */}
      {!isAuthenticated && (
        <section
          className="rounded-2xl p-8 sm:p-12 text-center border"
          style={{
            background: 'var(--navbar-top)',
            borderColor: 'transparent',
          }}
        >
          <h2 className="font-display font-black text-3xl text-white mb-3">
            Tham gia <span style={{ color: 'var(--brand)' }}>FNB HUB</span> ngay hôm nay
          </h2>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">Nền tảng sang nhượng, cho thuê và tuyển dụng ngành F&B hàng đầu Việt Nam</p>
          <Link to="/register" className="btn-brand inline-flex items-center gap-2 px-8 py-3 text-base rounded-xl">
            Đăng ký miễn phí <ArrowRight size={18} />
          </Link>
        </section>
      )}

      {/* ══════ DỊCH VỤ HỖ TRỢ ══════ */}
      <ServicesSection />
    </div>
  );
}



/* ─── Reusable section header ─── */
function SectionHeader({ label, title, to }: { label: string; title: string; to: string }) {
  return (
    <div className="flex items-end justify-between mb-5">
      <div>
        <div className="section-label">{label}</div>
        <h2 className="section-title">{title}</h2>
      </div>
      <Link to={to} className="flex items-center gap-1 text-sm font-semibold hover:opacity-80 transition-opacity" style={{ color: 'var(--brand)' }}>
        Xem thêm <ArrowRight size={14} />
      </Link>
    </div>
  );
}

/* ─── Services Section ─── */
const SERVICE_BUTTONS = [
  { icon: '⚖️', label: 'Tư vấn pháp lý', bg: '#f97316', link: '/dich-vu/tu-van-phap-ly' },
  { icon: '🛡️', label: 'Dịch vụ hỗ trợ\nATPF', bg: '#22c55e', link: '/dich-vu/atfp' },
  { icon: '🧾', label: 'Hỗ trợ kế toán\nnhà hàng', bg: '#06b6d4', link: '/dich-vu/ke-toan' },
  { icon: '🛒', label: 'Đặt hàng\nnguyên liệu', bg: '#3b82f6', link: '/dich-vu/nguyen-lieu' },
  { icon: '📦', label: 'Các dịch vụ\nkhác...', bg: '#6b7280', link: '/dich-vu/khac' },
];

const SERVICE_INFO = [
  {
    IconSvg: 'users',
    pre: 'Giới thiệu các doanh nghiệp thành viên của',
    title: 'Danh sách hội viên',
    sub: 'Hiệp hội F&B Việt Nam',
    link: '/dich-vu/hoi-vien',
  },
  {
    IconSvg: 'bar-chart',
    pre: 'Thống kê số liệu về',
    title: 'Khảo sát chuỗi nhà hàng F&B',
    sub: 'Chuỗi, cửa hàng, doanh thu',
    link: '/dich-vu/khao-sat-chuoi',
  },
  {
    IconSvg: 'mail',
    pre: 'Đăng ký nhận',
    title: 'Tài liệu & Báo cáo ngành',
    sub: 'Gửi yêu cầu qua email',
    link: '/dich-vu/tai-lieu',
  },
  {
    IconSvg: 'info',
    pre: 'Hướng dẫn quyền lợi hội viên',
    title: 'Thủ tục đăng ký thành viên',
    sub: 'Quy trình, thủ tục tham gia',
    link: '/dich-vu/dang-ky-hoi-vien',
  },
  {
    IconSvg: 'clock',
    pre: 'Lịch cập nhật dữ liệu tiếp theo',
    title: 'Khảo sát cửa hàng tiện lợi F&B',
    sub: 'Cập nhật tiếp: Thứ Sáu, 21/03',
    link: '/dich-vu/khao-sat-cua-hang',
  },
  {
    IconSvg: 'phone',
    pre: 'Tổng đài tư vấn toàn diện về',
    title: 'Trung tâm hỗ trợ F&B',
    sub: 'Kinh doanh F&B, nhượng quyền',
    link: '/dich-vu/ho-tro',
  },
];

/* Minimal SVG icons matching the screenshot's square-icon style */
function ServiceIcon({ type }: { type: string }) {
  const icons: Record<string, JSX.Element> = {
    users: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    'bar-chart': (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
        <line x1="18" y1="20" x2="18" y2="10" />
        <line x1="12" y1="20" x2="12" y2="4" />
        <line x1="6" y1="20" x2="6" y2="14" />
        <line x1="2" y1="20" x2="22" y2="20" />
      </svg>
    ),
    mail: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="M22 7l-10 7L2 7" />
      </svg>
    ),
    info: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="8" />
        <line x1="12" y1="12" x2="12" y2="16" />
      </svg>
    ),
    clock: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    phone: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.15 12 19.79 19.79 0 0 1 1.08 3.4 2 2 0 0 1 3.06 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9.1A16 16 0 0 0 13 15l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16v.92z" />
      </svg>
    ),
  };
  return icons[type] ?? null;
}

function ServicesSection() {
  return (
    <section className="overflow-hidden rounded-2xl border" style={{ borderColor: 'var(--border-c)', background: 'var(--surface-0)' }}>
      {/* ── Row 1: Coloured service buttons ── */}
      <div className="flex flex-wrap">
        {SERVICE_BUTTONS.map((s, i) => (
          <Link
            to={s.link}
            key={i}
            className="flex-1 min-w-[120px] flex items-center gap-3 px-5 py-4 text-white font-semibold text-sm text-left
                       transition-all hover:brightness-110 active:scale-95"
            style={{ background: s.bg }}
          >
            {/* White square icon bg */}
            <span className="w-9 h-9 shrink-0 rounded-lg bg-white/25 flex items-center justify-center text-lg leading-none">{s.icon}</span>
            <span className="whitespace-pre-line leading-snug">{s.label}</span>
          </Link>
        ))}
      </div>

      {/* ── Row 2: 3×2 info grid ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3" style={{ borderTop: '1px solid var(--border-c)' }}>
        {SERVICE_INFO.map((item, i) => (
          <Link
            to={item.link}
            key={i}
            className="flex gap-3 px-5 py-4 cursor-pointer transition-colors hover:bg-orange-50"
            style={{
              borderRight: i % 3 !== 2 ? '1px solid var(--border-c)' : undefined,
              borderBottom: i < 3 ? '1px solid var(--border-c)' : undefined,
            }}
          >
            {/* Square icon — blue pastel */}
            <div className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center text-blue-500" style={{ background: '#dbeafe', border: '1px solid #bfdbfe' }}>
              <ServiceIcon type={item.IconSvg} />
            </div>

            <div className="min-w-0">
              {/* Blue dot + small grey text */}
              <p className="flex items-start gap-1 text-xs leading-snug mb-0.5" style={{ color: 'var(--text-3)' }}>
                <span className="mt-[5px] w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                {item.pre}
              </p>
              {/* Bold blue title */}
              <p className="text-sm font-bold leading-snug text-blue-600 hover:underline">{item.title}</p>
              {/* Subtitle tag */}
              {item.sub && (
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>
                  {item.sub}
                </p>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
